import topic from "common/managers/topic";
import join from "common/helper/join";
/**
 * The Locking manager supports two different types of locks.
 *
 * Network locks: These are tracked automatically. Any network request that is ongoing
 * and hasn't been ignored will be force calls to checkLock to reject.
 *
 * Soft locks: These are registered with softLock().  These are things that are locked, but
 * there is a way to free the resources that are causing the problem.  They generally
 * support to ways to free resources: discard or resolve.  Discard must fix the problem
 * with no user interaction and do it right away.  The lock manager does not allow for discards
 * to fail.  Resolve can fail, and can take as long as they like.  This means that calls to checkLock
 * will stall until the soft lock is released.
 *
 * --Current implementation--
 * Any number of network requests can be tracked/ignored.
 * Any number of soft locks can be managed at once.
 * Only one checkLock call can be pending at one time. Previous checkLock() enforced if needed.
 *
 * --Topic Events--
 * lock/soft/prompt: Fired when checkLock() can't auto determine resolve/reject
 *
 *
 */
var Locking = kendo.Observable.extend({

    init: function() {
        kendo.Observable.fn.init.call(this);
        this._names = [];
    },

    /**
     * This can be used to get a one-off instance.
     * @returns {Locking}
     */
    getInstance: function() {
        return new Locking();
    },

    /** The number of ongoing requests that don't matter */
    _ignoreActiveCount: 0, //Set to 0 to enable hard locking for network requests

    /** True only when active request count matters */
    _enableLocking: false,

    /** Get the current count of ongoing requests that matter */
    _getLockCount: function() {
        return this._enableLocking && $ && $.active ? $.active - this._ignoreActiveCount : 0;
    },

    /**
     * Ignore a request
     *
     * @param request jquery request object to ignore
     */
    ignore: function(request) {
        this._ignoreActiveCount++;
        var me = this;
        request.always(function() {
            me._ignoreActiveCount--;
        });
    },

    /**
     * Turn on tracking of network requests
     */
    enableLocking: function() {
        this._enableLocking = true;
    },

    /** The number of soft locks currently tracked */
    _softLockCount: 0,

    /** The number of super-soft locks currently tracked. */
    _superSoftLockCount: 0,

    _names: [],

    /**
     * Lock navigation conditionally.
     * @param discard Callback when navigation continues destructively
     * @param resolve Callback when navigation temporarily stalled until soft locks released.  Must call considerLock when done.
     * @param name optional name to use to identify this lock
     * @param superSoft whether the lock is considered "super soft" - main use case is ignoring certain locks when switching workspaces in main.
     * @returns {{release: release}} release soft lock without calling discard
     */
    softLock: function(discard, resolve, name, superSoft) {
        var released = false;
        var me = this;
        var d = function() {
            discard();
            release(true);
        };
        var release = function(skipConsider) {
            if (released == false) {
                released = true;
                if (superSoft) {
                    me._superSoftLockCount--;
                } else {
                    me._softLockCount--;
                }
                me.unbind("discard", d);
                me.unbind("resolve", resolve);
                if (name) {
                    var idx = me._names.indexOf(name);
                    if (idx > -1) {
                        me._names.splice(idx, 1);
                    }
                }
                if (skipConsider === undefined || skipConsider === false) {
                    me._considerLock();
                }
            }
        };
        this.bind("discard", d);
        this.bind("resolve", resolve);
        if (superSoft) {
            this._superSoftLockCount++;
        } else {
            this._softLockCount++;
        }
        if (name) {
            this._names.push(name);
        }
        return {
            release: release
        }
    },

    /**
     * Can be called after checkLock.  Should be called when async soft lock check should allow navigation
     * and throw away changes.
     */
    discardLock: function() {
        this.trigger("discard");
        if (this._softDeferred) {
            this._softDeferred.resolve();
            delete this._softDeferred;
        }
    },

    /**
     * Can be called after checkLock.  Should be called when aysnc soft lock check should disallow navigation
     */
    enforceLock: function() {
        if (this._softDeferred) {
            this._softDeferred.reject();
            delete this._softDeferred;
        }
    },

    /**
     * If a checkLock call is pending and there are NO hard or soft locks, the checkLock call is resolved.
     * Otherwise, this does nothing.
     * @private
     */
    _considerLock: function() {
        if (this._softDeferred && this._getLockCount() == 0 && this._softLockCount == 0) {
            this._softDeferred.resolve();
            delete this._softDeferred;
        }
    },

    /**
     * This should be called when the user wants to resolve the locking condition, then navigate the page.
     */
    resolveLock: function() {
        this.trigger("resolve", {
            enforce: $.proxy(this.enforceLock, this)
        });
    },

    /**
     * Get the locked state.  Returns true if navigation is locked in some way.
     * @param considerSuperSoft whether to consider super-soft locks (those that allow panels to close, but prevent logout)
     */
    lockedState: function(considerSuperSoft) {
        return this._getLockCount() > 0 || this._softLockCount > 0 || (considerSuperSoft && this._superSoftLockCount > 0);
    },

    /**
     * Checks if navigation should be locked or not.  Returns a promise that will be resolved if navigation may continue
     * and rejected if navigation must be prevented.
     * @param message Optional message to display to the user
     * @param ignoreSuperSoft whether to ignore super soft locks - main use case is switching workspaces in main
     * @returns {*}
     */
    checkLock: function(message, ignoreSuperSoft) {
        this.enforceLock();
        var def = $.Deferred();
        if (this._getLockCount() > 0) {
            def.reject();
        } else if (this._softLockCount > 0 || (this._superSoftLockCount > 0 && !ignoreSuperSoft)) {
            this._softDeferred = def;
            topic.publish("lock/soft/prompt", this, message);
        } else {
            def.resolve();
        }
        return def.promise();
    },

    getNames: function() {
        return join(this._names);
    }
});

export default new Locking();